import { useContext, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router';
import { MapContext } from '../contexts/MapContext';
import GroupedTypeahead from './GroupedTypeahead';
import { sidePanelTypeaheadStyles } from './basics/StandardTypeahead';
import { CreateCropVarietyMutation } from '../mutations';
import { MULTIPLE, NONE } from '../constants/fields';
import { UnstyledButton } from './basics/UnstyledButton';
import { captureEvent } from '../utils/analytics';
import { showError } from '../utils/toast';

const StyledTypeahead = styled(GroupedTypeahead)`
  ${sidePanelTypeaheadStyles}
`;

const inputProps = {
  className: 'pr-4', // Add padding so ellipsis-overflow text is not hidden by clear button
};

const CropVarietySelector = ({
  id,
  cropId,
  cropVarietyId,
  onCropVarietySelect,
}) => {
  const {
    crops: { availableCrops: cropMap, updateCrop },
  } = useContext(MapContext);
  const typeaheadRef = useRef();

  const crop = cropMap[cropId];
  const cropVariety = useMemo(
    () => crop?.cropVarieties?.find((v) => v.id === cropVarietyId),
    [crop, cropVarietyId],
  );

  const { farmId } = useParams();
  const [pendingCropVariety, setPendingCropVariety] = useState(null);

  const [createCropVariety] = useMutation(CreateCropVarietyMutation, {
    onCompleted: ({ upsertFarmCrop: { result, errors } }) => {
      if (errors) {
        showError(errors);
      }
      if (result) {
        const { updatedAt, cropVarieties } = result;
        updateCrop(result.id, { updatedAt, cropVarieties });
        const addedCropVariety = cropVarieties.find(
          ({ name }) => name === pendingCropVariety.name,
        );
        if (addedCropVariety) {
          onCropVarietySelect(addedCropVariety);
        }
      }
      setPendingCropVariety(null);
    },
    onError: (e) => {
      showError(e);
      setPendingCropVariety(null);
    },
  });

  const onChange = async ([option]) => {
    if (!crop) return;
    if (option?.customOption) {
      setPendingCropVariety(option);
      captureEvent('crop-variety-create', { location: 'variety-selector' });
      await createCropVariety({
        variables: {
          input: {
            id: crop.id,
            farmId,
            updatedAt: crop.updatedAt,
            cropVarieties: [{ name: option.name }],
          },
        },
      });
    } else {
      onCropVarietySelect(option);
    }
  };

  const placeholder = pendingCropVariety
    ? 'Loading...'
    : cropVariety?.name || (cropVarietyId === MULTIPLE ? MULTIPLE : NONE);

  return (
    <StyledTypeahead
      id={id}
      align="right"
      allowNew
      clearTextOnFocus
      inputProps={inputProps}
      labelKey="name"
      newSelectionPrefix="Add variety: "
      newSelectionPrompt="Type to add variety..."
      options={crop?.cropVarieties || []}
      placeholder={placeholder}
      ref={typeaheadRef}
      selectedOption={cropVariety}
      size="sm"
      onChange={onChange}
    >
      {({ onClear, selected }) => (
        <div className="rbt-aux">
          {!!selected.length && (
            <UnstyledButton className="rbt-close font-large" onClick={onClear}>
              ×
            </UnstyledButton>
          )}
        </div>
      )}
    </StyledTypeahead>
  );
};

export default CropVarietySelector;
